import React, { useState } from "react";
import makeStyles from "utils/makeStyles";
import withStyles from "utils/withStyles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import classNames from "classnames";
import TablePaginationActions from "../StaticSortTable/TablePaginationActions";
import { stableSort, getSorting } from "helpers";
import Loader from "../Loader";
import EnhancedTableHead from "components/Common/StaticSortTable/EnhancedTableHead";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    position: "relative",
    width: "100%",
    overflowX: "auto",
  },
  table: {
    minWidth: 300,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  tableHead: {
    backgroundColor: "#ECF0F1",
  },
  headCell: {
    fontWeight: "bold",
    fontSize: "12px",
  },
  pagination: {
    fontSize: "10px",
  },
  tableRowSelected: {
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: "#ECF0F1",
    },
  },
  tableCell: {
    fontSize: "12px",
  },
  selected: {},
}));

const TableGenerator = ({ selectedRecords = [],
  fields = [],
  data = [],
  onPageChange = () => {},
  onChangeSelected = () => {},
  rowOnePage = 10,
  radio = false,
  size = "medium",
  sensorTable = false,
  onRowClick = () => {},
  ...props
}) => {
  const classes = useStyles();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("calories");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowOnePage);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selectedRecords.findIndex(
      (val) => val.id === name.id
    );
    let newSelected = [];

    if (radio) {
      if (selectedIndex) {
        newSelected = [name];
      } else {
        newSelected = [];
      }
    } else {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedRecords, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedRecords.slice(1));
      } else if (selectedIndex === selectedRecords.length - 1) {
        newSelected = newSelected.concat(selectedRecords.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedRecords.slice(0, selectedIndex),
          selectedRecords.slice(selectedIndex + 1)
        );
      }
    }
    onChangeSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (record) =>
    selectedRecords.findIndex((x) => x.id === record.id) !== -1;

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);
  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Loader visible={props.loader} />
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={size}
          aria-label="enhanced table"
          style={{ minHeight: 122 }}
        >
          <EnhancedTableHead
            classes={classes}
            numSelected={selectedRecords.length}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={data.length}
            fields={fields}
          />
          <TableBody>
            {stableSort(data, getSorting(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((record, index) => {
                const isItemSelected = isSelected(record);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={record.name}
                    classes={{ selected: classes.selected }}
                    className={classes.tableRowSelected}
                    selected={isItemSelected}
                    style={{ height: 53 }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        style={{
                          visibility: sensorTable && "hidden",
                          zIndex: 2000000,
                        }}
                        onClick={(event) => handleClick(event, record)}
                        checked={isItemSelected}
                        slotProps={{ input: { "aria-labelledby": labelId } }}
                      />
                    </TableCell>
                    {fields
                      .filter((x) => x.visible)
                      .map((field) => (
                        <TableCell
                          key={field.key}
                          onClick={() => onRowClick(record, index)}
                          className={classNames(
                            classes.tableCell,
                            field.className
                          )}
                          align="left"
                        >
                          {field.render && typeof field.render === "function"
                            ? field.render(record[field.key], record, index)
                            : record[field.key]}
                        </TableCell>
                      ))}
                  </TableRow>
                );
              })}
            {emptyRows > 0 && (
              <TableRow>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[3, 5, 10, 25, 50, 100]}
          colSpan={3}
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          slotProps={{ select: { "aria-label": "rows per page" } }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      </Paper>
    </div>
  );
};


export default withStyles({}, { withTheme: true })(TableGenerator);
